import { useCallback, useEffect, useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Stepper } from '@/components/ui/stepper'
import { WizardNavigation } from '@/components/PlanWizard/WizardNavigation'
import {
  prepareBulkCommitmentLetters,
  type BulkPlanPrepare,
  type CommitmentLetterContact,
  type CommitmentLetterInputs,
} from '@/api/commitmentLetters'
import { useBulkCommitmentLetterExport } from '@/hooks/useBulkCommitmentLetterExport'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  commitmentLetterSchema,
  defaultFormValues,
  STEP_FIELDS,
  toInputs,
  type CommitmentLetterFormInput,
} from './schema'
import { StepDetails } from './StepDetails'
import { StepContacts } from './StepContacts'
import { BulkNotice, BulkStepReview, BulkStepTransit } from './BulkSteps'

const STEPS = [
  { id: 'details', label: 'Details' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'transit', label: 'Ports & Transit' },
  { id: 'review', label: 'Review' },
]
const TRANSIT_STEP = 2
const REVIEW_STEP = 3
const MAX_CONTACTS = 10

/** planId -> vendor_key -> Strategy Code override. */
type PlanStrategyCodes = Record<string, Record<string, string>>

interface BulkCommitmentLetterWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planIds: string[]
}

/** Distinct plan owners, used to prefill the contact lists. */
function ownerContacts(plans: BulkPlanPrepare[]): CommitmentLetterContact[] {
  const byEmail = new Map<string, CommitmentLetterContact>()
  for (const plan of plans) {
    const owner = plan.prepare.defaults.contacts[0]
    if (owner && !byEmail.has(owner.email.toLowerCase())) {
      byEmail.set(owner.email.toLowerCase(), owner)
    }
  }
  return [...byEmail.values()].slice(0, MAX_CONTACTS)
}

export function BulkCommitmentLetterWizard({
  open,
  onOpenChange,
  planIds,
}: BulkCommitmentLetterWizardProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [acknowledgedMissing, setAcknowledgedMissing] = useState(false)
  const [reviewInputs, setReviewInputs] =
    useState<CommitmentLetterInputs | null>(null)
  const [defaultsApplied, setDefaultsApplied] = useState(false)
  const [strategyCodes, setStrategyCodes] = useState<PlanStrategyCodes>({})

  const form = useForm<CommitmentLetterFormInput>({
    resolver: zodResolver(
      commitmentLetterSchema
    ) as unknown as Resolver<CommitmentLetterFormInput>,
    defaultValues: defaultFormValues(),
    mode: 'onTouched',
  })

  // All plans with their default Strategy Codes ...
  const prepare = useQuery({
    queryKey: ['commitment-letter-bulk-prepare', planIds],
    queryFn: () =>
      prepareBulkCommitmentLetters(
        planIds.map(id => ({ plan_id: id, strategy_codes: {} }))
      ),
    enabled: open && planIds.length > 0,
  })

  // ... and only the plans whose codes were overridden are re-prepared.
  const overriddenPlanIds = Object.keys(strategyCodes).filter(
    id => Object.keys(strategyCodes[id]).length > 0
  )
  const overrides = useQueries({
    queries: overriddenPlanIds.map(planId => ({
      queryKey: [
        'commitment-letter-bulk-prepare-plan',
        planId,
        strategyCodes[planId],
      ],
      queryFn: () =>
        prepareBulkCommitmentLetters([
          { plan_id: planId, strategy_codes: strategyCodes[planId] },
        ]).then(r => r.plans[0] ?? null),
      placeholderData: keepPreviousData,
    })),
  })
  const isRefreshing = overrides.some(q => q.isFetching)

  const overridden = new Map(
    overrides
      .map(q => q.data)
      .filter((p): p is BulkPlanPrepare => !!p)
      .map(p => [p.plan_id, p])
  )
  const plans = (prepare.data?.plans ?? []).map(
    p => overridden.get(p.plan_id) ?? p
  )

  useEffect(() => {
    if (prepare.error) toast.error(getErrorMessage(prepare.error))
  }, [prepare.error])

  // Prefill contacts with the plan owners once, without overriding edits.
  useEffect(() => {
    if (!prepare.data || defaultsApplied) return
    const owners = ownerContacts(prepare.data.plans)
    if (owners.length > 0) {
      for (const name of ['sampleContacts', 'confirmationContacts'] as const) {
        const current = form.getValues(name)
        if (current.length === 1 && !current[0].name && !current[0].email) {
          form.setValue(name, owners)
        }
      }
    }
    setDefaultsApplied(true)
  }, [prepare.data, defaultsApplied, form])

  const {
    isProcessing,
    isCompleted,
    isError,
    progress,
    exportBulkCommitmentLetters,
    reset: resetExport,
  } = useBulkCommitmentLetterExport()

  const resetWizard = useCallback(() => {
    setStep(0)
    setAcknowledgedMissing(false)
    setReviewInputs(null)
    setDefaultsApplied(false)
    setStrategyCodes({})
    form.reset(defaultFormValues())
    resetExport()
    queryClient.removeQueries({ queryKey: ['commitment-letter-bulk-prepare'] })
    queryClient.removeQueries({
      queryKey: ['commitment-letter-bulk-prepare-plan'],
    })
    queryClient.removeQueries({ queryKey: ['commitment-letter-bulk-preview'] })
  }, [form, resetExport, queryClient])

  useEffect(() => {
    if (isCompleted) {
      resetWizard()
      onOpenChange(false)
    }
  }, [isCompleted, resetWizard, onOpenChange])

  // Vendors whose Strategy Code is not in the calendar get blank dates by
  // design, so only calendar strategies without rows need "Generate anyway".
  const missingDates = plans.flatMap(p =>
    p.prepare.vendors
      .filter(v => v.transit_rows.length === 0 && v.strategy_has_calendar)
      .map(() => p.plan_id)
  )
  const plansMissingDates = new Set(missingDates)

  const handleStrategyChange = (
    planId: string,
    vendorKey: string,
    code: string | null
  ) => {
    setStrategyCodes(prev => {
      const next = { ...(prev[planId] ?? {}) }
      if (code) next[vendorKey] = code
      else delete next[vendorKey]
      return { ...prev, [planId]: next }
    })
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (isProcessing && !newOpen) return
    if (!newOpen) resetWizard()
    onOpenChange(newOpen)
  }

  const handleContinue = async () => {
    const valid = await form.trigger(STEP_FIELDS[step])
    if (!valid) return
    if (step === TRANSIT_STEP) {
      const parsed = commitmentLetterSchema.safeParse(form.getValues())
      if (!parsed.success) {
        setStep(0)
        return
      }
      setReviewInputs(toInputs(parsed.data))
    }
    setStep(s => Math.min(s + 1, REVIEW_STEP))
  }

  const handleSubmit = () => {
    if (!reviewInputs) return
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { strategy_codes, ...inputs } = reviewInputs
    exportBulkCommitmentLetters({
      ...inputs,
      plans: plans.map(p => ({
        plan_id: p.plan_id,
        strategy_codes: strategyCodes[p.plan_id] ?? {},
      })),
    })
  }

  const detailsBlocked = !prepare.data || plans.length === 0
  const transitBlocked =
    detailsBlocked ||
    isRefreshing ||
    (missingDates.length > 0 && !acknowledgedMissing)

  const canContinue =
    step === TRANSIT_STEP
      ? !transitBlocked
      : step === 0
        ? !detailsBlocked
        : true

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-4xl flex-col gap-5">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Create Commitment Letters ({planIds.length} plan
            {planIds.length === 1 ? '' : 's'})
          </DialogTitle>
        </DialogHeader>

        <Stepper steps={STEPS} currentStep={step} />

        {isProcessing || isError ? (
          <div className="flex flex-col gap-3 py-8">
            <Progress value={progress} />
            <p className="text-center text-sm text-muted-foreground">
              {isProcessing ? 'Generating letters...' : 'Export failed'}
            </p>
          </div>
        ) : prepare.isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Loading {planIds.length} plan(s)...
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-1">
            {prepare.isError && (
              <p className="text-sm text-destructive">
                Failed to load plan data for the commitment letters.
              </p>
            )}
            {step === 0 && prepare.data && (
              <>
                <BulkNotice
                  plans={plans}
                  skippedPlans={prepare.data.skipped_plans}
                />
                <StepDetails form={form} data={undefined} isLoading={false} />
              </>
            )}
            {step === 1 && <StepContacts form={form} />}
            {step === TRANSIT_STEP && (
              <BulkStepTransit
                plans={plans}
                isRefreshing={isRefreshing}
                onStrategyChange={handleStrategyChange}
                vendorsMissingDates={missingDates.length}
                plansMissingDates={plansMissingDates}
                acknowledgedMissing={acknowledgedMissing}
                onAcknowledgeMissing={setAcknowledgedMissing}
              />
            )}
            {step === REVIEW_STEP && reviewInputs && (
              <BulkStepReview
                plans={plans}
                inputs={reviewInputs}
                strategyCodes={strategyCodes}
              />
            )}
          </div>
        )}

        <WizardNavigation
          onBack={() => setStep(s => Math.max(s - 1, 0))}
          canGoBack={step > 0 && !isProcessing}
          onContinue={handleContinue}
          onSubmit={handleSubmit}
          canContinue={canContinue && !isProcessing}
          isLastStep={step === REVIEW_STEP}
          isSubmitting={isProcessing}
          submitLabel="Download .ZIP"
        />
      </DialogContent>
    </Dialog>
  )
}
